<template>
  <section class="estrut-comments">
    <header class="estrut-comments__hero">
      <div class="estrut-comments__hero-main">
        <div class="estrut-comments__meta">
          <div class="estrut-comments__icon-wrap">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
            </svg>
          </div>
          <div class="estrut-comments__meta-text">
            <div class="estrut-comments__title-row">
              <h3 class="estrut-comments__title">{{ t('estruturacao.lessons.commentsTitle') }}</h3>
              <span v-if="totalCount > 0" class="estrut-comments__count">{{ totalCount }}</span>
            </div>
            <p class="estrut-comments__subtitle">{{ t('estruturacao.lessons.commentsSubtitle') }}</p>
          </div>
        </div>
      </div>
    </header>

    <footer class="estrut-comments__composer">
      <div class="estrut-comments__composer-avatar" aria-hidden="true">
        <img v-if="currentUserPhoto" :src="currentUserPhoto" :alt="currentUserName" />
        <span v-else class="estrut-comments__initials">{{ initials(currentUserName) }}</span>
      </div>
      <div class="estrut-comments__composer-main">
        <textarea
          ref="textareaRef"
          v-model="draft"
          class="estrut-comments__input"
          rows="2"
          :placeholder="t('estruturacao.lessons.commentPlaceholder')"
          @keydown.enter.exact.prevent="send"
        />
        <div class="estrut-comments__composer-actions">
          <p class="estrut-comments__hint">{{ t('estruturacao.lessons.enterToSend') }}</p>
          <button
            type="button"
            class="estrut-comments__send"
            :disabled="sending || !draft.trim()"
            @click="send"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            {{ t('estruturacao.lessons.sendComment') }}
          </button>
        </div>
      </div>
    </footer>

    <div class="estrut-comments__feed">
      <div ref="listRef" class="estrut-comments__list">
        <div v-if="loading" class="estrut-comments__state">
          <span class="estrut-comments__spinner" aria-hidden="true" />
          {{ t('common.loading') }}
        </div>

        <div v-else-if="!comments.length" class="estrut-comments__empty">
          <div class="estrut-comments__empty-icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <p>{{ t('estruturacao.lessons.noComments') }}</p>
        </div>

        <template v-else>
          <article
            v-for="comment in comments"
            :key="comment.id"
            class="estrut-comments__msg"
            :class="{ 'estrut-comments__msg--own': isOwn(comment) }"
          >
            <div
              class="estrut-comments__avatar"
              :class="{ 'estrut-comments__avatar--own': isOwn(comment) }"
              :title="comment.usuarioNome"
            >
              <img
                v-if="photoUrl(comment)"
                :src="photoUrl(comment)"
                :alt="comment.usuarioNome"
                loading="lazy"
                @error="onAvatarError(comment.id)"
              />
              <span v-else class="estrut-comments__initials">{{ initials(comment.usuarioNome) }}</span>
            </div>

            <div class="estrut-comments__body">
              <div class="estrut-comments__bubble">
                <header class="estrut-comments__bubble-head">
                  <span class="estrut-comments__name-row">
                    <span class="estrut-comments__name">{{ comment.usuarioNome }}</span>
                    <span
                      v-if="comment.usuarioSuperAdmin"
                      class="estrut-comments__sa-badge"
                      :title="t('estruturacao.lessons.commentSuperAdmin')"
                      :aria-label="t('estruturacao.lessons.commentSuperAdmin')"
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </span>
                  </span>
                  <span v-if="isOwn(comment)" class="estrut-comments__you">{{ t('estruturacao.lessons.commentYou') }}</span>
                  <time class="estrut-comments__date" :datetime="dateIso(comment.dataCriacao)">
                    {{ formatCommentDate(comment.dataCriacao) }}
                  </time>
                </header>
                <p class="estrut-comments__text">{{ comment.mensagem }}</p>
              </div>
            </div>
          </article>
        </template>
      </div>

      <button
        v-if="hasMore && !loading"
        type="button"
        class="estrut-comments__load-more"
        :disabled="loadingMore"
        @click="loadMore"
      >
        <span v-if="loadingMore" class="estrut-comments__spinner" aria-hidden="true" />
        {{ t('estruturacao.lessons.commentsLoadMore') }}
      </button>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../../stores/auth'
import { fetchComentarios, postComentario } from '../../services/estruturacaoApi'
import { formatBackendDateTime, parseBackendDateTime } from '../../utils/format'
import { getUserInitials, getUserPhotoUrl } from '../../utils/userDisplay'

const PAGE_SIZE = 20

const props = defineProps({
  aulaId: { type: Number, required: true }
})

const { t, locale } = useI18n()
const authStore = useAuthStore()

const comments = ref([])
const totalCount = ref(0)
const hasMore = ref(false)
const loading = ref(true)
const loadingMore = ref(false)
const sending = ref(false)
const draft = ref('')
const listRef = ref(null)
const textareaRef = ref(null)
const brokenAvatars = ref(new Set())

const currentUserName = computed(() =>
  authStore.user?.nomeCompleto || authStore.user?.nome || ''
)
const currentUserPhoto = computed(() => getUserPhotoUrl(authStore.user))

function isOwn(comment) {
  return comment.usuarioId === authStore.user?.id
}

function initials(name) {
  return getUserInitials({ nomeCompleto: name })
}

function photoUrl(comment) {
  if (brokenAvatars.value.has(comment.id)) return null
  return getUserPhotoUrl({
    fotoPerfil: comment.usuarioFotoPerfil,
    usuarioFotoPerfil: comment.usuarioFotoPerfil
  })
}

function onAvatarError(commentId) {
  const next = new Set(brokenAvatars.value)
  next.add(commentId)
  brokenAvatars.value = next
}

function formatCommentDate(value) {
  return formatBackendDateTime(value, locale.value)
}

function dateIso(value) {
  const d = parseBackendDateTime(value)
  return d ? d.toISOString() : undefined
}

function applyPageResponse(data, append = false) {
  const items = data?.items ?? (Array.isArray(data) ? data : [])
  if (append) {
    comments.value = [...comments.value, ...items]
  } else {
    comments.value = items
  }
  totalCount.value = data?.total ?? items.length
  hasMore.value = data?.hasMore ?? false
}

async function loadComments() {
  loading.value = true
  brokenAvatars.value = new Set()
  try {
    const { data } = await fetchComentarios(props.aulaId, { limit: PAGE_SIZE, offset: 0 })
    applyPageResponse(data, false)
  } catch {
    comments.value = []
    totalCount.value = 0
    hasMore.value = false
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (loadingMore.value || !hasMore.value) return
  loadingMore.value = true
  const prevHeight = listRef.value?.scrollHeight ?? 0
  try {
    const { data } = await fetchComentarios(props.aulaId, {
      limit: PAGE_SIZE,
      offset: comments.value.length
    })
    applyPageResponse(data, true)
    await nextTick()
    if (listRef.value) {
      const delta = listRef.value.scrollHeight - prevHeight
      listRef.value.scrollTop += delta
    }
  } catch (e) {
    console.warn('[EstruturacaoComments] loadMore', e)
  } finally {
    loadingMore.value = false
  }
}

function scrollToTop() {
  if (listRef.value) {
    listRef.value.scrollTop = 0
  }
}

async function send() {
  const text = draft.value.trim()
  if (!text || sending.value) return
  sending.value = true
  try {
    const { data } = await postComentario(props.aulaId, text)
    comments.value.unshift(data)
    totalCount.value += 1
    draft.value = ''
    await nextTick()
    scrollToTop()
    textareaRef.value?.focus()
  } catch (e) {
    console.warn('[EstruturacaoComments]', e)
  } finally {
    sending.value = false
  }
}

watch(() => props.aulaId, () => {
  loadComments()
})

onMounted(() => {
  loadComments()
})
</script>

<style scoped>
.estrut-comments {
  display: flex;
  flex-direction: column;
  min-height: 480px;
  max-height: 680px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  overflow: hidden;
}

.estrut-comments__hero {
  flex-shrink: 0;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-primary);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--accent-primary) 8%, var(--bg-secondary)),
    var(--bg-secondary)
  );
}

.estrut-comments__hero-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.estrut-comments__meta {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.estrut-comments__icon-wrap {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: #fff;
  box-shadow: 0 4px 12px color-mix(in srgb, #4f46e5 35%, transparent);
}

.estrut-comments__icon-wrap svg {
  width: 22px;
  height: 22px;
}

.estrut-comments__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.estrut-comments__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
}

.estrut-comments__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--accent-primary);
  background: color-mix(in srgb, var(--accent-primary) 14%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent-primary) 22%, transparent);
}

.estrut-comments__subtitle {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.4;
}

.estrut-comments__composer {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-primary);
}

.estrut-comments__composer-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--accent-primary), #7c3aed);
  color: #fff;
}

.estrut-comments__composer-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.estrut-comments__composer-main {
  flex: 1;
  min-width: 0;
}

.estrut-comments__input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.875rem;
  line-height: 1.45;
  resize: vertical;
  min-height: 56px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.estrut-comments__input:focus {
  outline: none;
  border-color: color-mix(in srgb, var(--accent-primary) 45%, var(--border-primary));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-primary) 12%, transparent);
}

.estrut-comments__composer-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.estrut-comments__hint {
  margin: 0;
  font-size: 0.6875rem;
  color: var(--text-tertiary);
}

.estrut-comments__send {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background: linear-gradient(
    135deg,
    var(--accent-primary),
    color-mix(in srgb, var(--accent-primary) 72%, #7c3aed)
  );
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px color-mix(in srgb, var(--accent-primary) 28%, transparent);
  transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s;
}

.estrut-comments__send svg {
  width: 16px;
  height: 16px;
}

.estrut-comments__send:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px color-mix(in srgb, var(--accent-primary) 36%, transparent);
}

.estrut-comments__send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.estrut-comments__feed {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary);
}

.estrut-comments__list {
  flex: 1;
  min-height: 320px;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.estrut-comments__state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 16px;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.estrut-comments__spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-primary);
  border-top-color: var(--accent-primary);
  border-radius: 50%;
  animation: estrut-cmt-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes estrut-cmt-spin {
  to { transform: rotate(360deg); }
}

.estrut-comments__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 36px 20px;
  text-align: center;
  border-radius: 4px;
  border: 1px dashed var(--border-primary);
  background: var(--bg-primary);
}

.estrut-comments__empty-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--text-tertiary);
  background: color-mix(in srgb, var(--accent-primary) 8%, var(--bg-secondary));
}

.estrut-comments__empty-icon svg {
  width: 26px;
  height: 26px;
}

.estrut-comments__empty p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
  max-width: 280px;
}

.estrut-comments__msg {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  max-width: 100%;
}

.estrut-comments__msg--own {
  flex-direction: row-reverse;
}

.estrut-comments__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: #fff;
  border: 2px solid var(--bg-primary);
  box-shadow: 0 2px 8px color-mix(in srgb, #3b82f6 25%, transparent);
}

.estrut-comments__avatar--own {
  background: linear-gradient(135deg, var(--accent-primary), color-mix(in srgb, var(--accent-primary) 65%, #7c3aed));
  box-shadow: 0 2px 8px color-mix(in srgb, var(--accent-primary) 30%, transparent);
}

.estrut-comments__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.estrut-comments__initials {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.estrut-comments__body {
  flex: 1;
  min-width: 0;
  max-width: min(78%, 520px);
}

.estrut-comments__msg--own .estrut-comments__body {
  display: flex;
  justify-content: flex-end;
}

.estrut-comments__bubble {
  padding: 10px 14px;
  border-radius: 4px 12px 12px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  box-shadow: 0 1px 3px color-mix(in srgb, #000 6%, transparent);
}

.estrut-comments__msg--own .estrut-comments__bubble {
  border-radius: 12px 4px 12px 12px;
  background: color-mix(in srgb, var(--accent-primary) 10%, var(--bg-primary));
  border-color: color-mix(in srgb, var(--accent-primary) 22%, var(--border-primary));
}

.estrut-comments__bubble-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin-bottom: 6px;
}

.estrut-comments__name-row {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.estrut-comments__name {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--text-primary);
}

.estrut-comments__sa-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  color: #7c3aed;
  background: color-mix(in srgb, #7c3aed 14%, transparent);
  flex-shrink: 0;
}

.estrut-comments__sa-badge svg {
  width: 12px;
  height: 12px;
}

.estrut-comments__you {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 4px;
  color: var(--accent-primary);
  background: color-mix(in srgb, var(--accent-primary) 12%, transparent);
}

.estrut-comments__date {
  margin-left: auto;
  font-size: 0.6875rem;
  color: var(--text-tertiary);
  white-space: nowrap;
}

.estrut-comments__msg--own .estrut-comments__date {
  margin-left: 0;
}

.estrut-comments__text {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-primary);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.estrut-comments__load-more {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: calc(100% - 40px);
  margin: 0 20px 16px;
  padding: 10px 16px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}

.estrut-comments__load-more:hover:not(:disabled) {
  border-color: color-mix(in srgb, var(--accent-primary) 35%, var(--border-primary));
  color: var(--accent-primary);
  background: color-mix(in srgb, var(--accent-primary) 6%, var(--bg-primary));
}

.estrut-comments__load-more:disabled {
  opacity: 0.65;
  cursor: wait;
}

body.tema_escura .estrut-comments__input {
  background: var(--bg-tertiary, var(--bg-secondary));
}
</style>
